import { useState, useEffect } from "react";
import API from "@/lib/api";
import { FileText, ChevronDown, ChevronUp, Download } from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

function formatKES(num) {
  if (num == null) return "KES 0";
  return `KES ${Number(num).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function PayslipsPage() {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    API.get("/payslips").then(({ data }) => setPayslips(data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setExpanded(expanded === id ? null : id);

  return (
    <div className="space-y-6" data-testid="payslips-page">
      <h1 className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-950" style={{ fontFamily: "Outfit, sans-serif" }}>
        Payslips
      </h1>

      {loading ? (
        <div className="text-center py-12 text-sm text-slate-500">Loading payslips...</div>
      ) : payslips.length === 0 ? (
        <div className="text-center py-12 border border-slate-200 rounded-sm">
          <FileText className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No payslips yet. Process payroll to generate payslips.</p>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Employee</TableHead>
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden sm:table-cell">Month</TableHead>
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium text-right hidden md:table-cell">Gross</TableHead>
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium text-right hidden md:table-cell">Deductions</TableHead>
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium text-right">Net Pay</TableHead>
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payslips.map((p) => (
                <PayslipRow key={p.id} payslip={p} expanded={expanded === p.id} onToggle={() => toggle(p.id)} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function PayslipRow({ payslip: p, expanded, onToggle }) {
  const handleDownloadPdf = async (e) => {
    e.stopPropagation();
    try {
      const response = await API.get(`/payslips/${p.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `payslip_${p.employee_name?.replace(/\s+/g, "_")}_${p.month}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF download failed:", err);
    }
  };

  return (
    <>
      <TableRow
        className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer"
        onClick={onToggle}
        data-testid={`payslip-row-${p.id}`}
      >
        <TableCell className="px-4 py-3">
          <div className="font-medium text-sm text-slate-900">{p.employee_name}</div>
          <div className="text-xs text-slate-500">{p.department}</div>
        </TableCell>
        <TableCell className="px-4 py-3 text-sm text-slate-700 hidden sm:table-cell">{p.month}</TableCell>
        <TableCell className="px-4 py-3 text-sm text-slate-700 text-right font-mono hidden md:table-cell">{formatKES(p.gross_pay)}</TableCell>
        <TableCell className="px-4 py-3 text-sm text-[#991B1B] text-right font-mono hidden md:table-cell">{formatKES(p.total_deductions)}</TableCell>
        <TableCell className="px-4 py-3 text-sm text-[#065F46] text-right font-mono font-medium">{formatKES(p.net_pay)}</TableCell>
        <TableCell className="px-4 py-3">
          <div className="flex items-center gap-1">
            <button
              data-testid={`download-pdf-${p.id}`}
              onClick={handleDownloadPdf}
              className="p-1.5 hover:bg-[#002FA7]/10 rounded-sm transition-colors text-slate-500 hover:text-[#002FA7]"
              title="Download PDF"
            >
              <Download className="h-3.5 w-3.5" />
            </button>
            {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow data-testid={`payslip-detail-${p.id}`}>
          <TableCell colSpan={6} className="px-4 py-4 bg-slate-50">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2">Earnings</h4>
                <div className="space-y-1.5">
                  <div className="flex justify-between"><span className="text-slate-600">Basic Salary</span><span className="font-mono text-slate-900">{formatKES(p.gross_salary)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">Allowances</span><span className="font-mono text-slate-900">{formatKES(p.allowances)}</span></div>
                  <div className="flex justify-between border-t border-slate-200 pt-1.5"><span className="font-medium text-slate-900">Gross Pay</span><span className="font-mono font-medium text-slate-900">{formatKES(p.gross_pay)}</span></div>
                </div>
              </div>
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2">Statutory Deductions</h4>
                <div className="space-y-1.5">
                  <div className="flex justify-between"><span className="text-slate-600">NSSF</span><span className="font-mono text-[#991B1B]">{formatKES(p.nssf?.total)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">SHIF/SHA</span><span className="font-mono text-[#991B1B]">{formatKES(p.shif)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">Housing Levy</span><span className="font-mono text-[#991B1B]">{formatKES(p.housing_levy)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">PAYE</span><span className="font-mono text-[#991B1B]">{formatKES(p.paye?.net_paye)}</span></div>
                  {p.helb > 0 && <div className="flex justify-between"><span className="text-slate-600">HELB</span><span className="font-mono text-[#991B1B]">{formatKES(p.helb)}</span></div>}
                  <div className="flex justify-between border-t border-slate-200 pt-1.5"><span className="font-medium text-slate-900">Total Deductions</span><span className="font-mono font-medium text-[#991B1B]">{formatKES(p.total_deductions)}</span></div>
                </div>
              </div>
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2">Net Pay</h4>
                <div className="bg-[#ECFDF5] border border-[#10B981] rounded-sm p-4">
                  <div className="text-2xl font-medium text-[#065F46] font-mono">{formatKES(p.net_pay)}</div>
                  <div className="text-xs text-[#065F46] mt-1">Take-home for {p.month}</div>
                </div>
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
